import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, TextField, MenuItem, Button,
  TablePagination, IconButton, Tooltip, CircularProgress, Alert,
  InputAdornment, Stack, Select, FormControl, InputLabel, SelectChangeEvent,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  TouchApp as ActionIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Logout as LogoutIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';

interface LoginLog {
  id: string;
  user_id: string | null;
  username: string;
  display_name: string | null;
  ip_address: string;
  user_agent: string | null;
  status: string;
  failure_reason: string | null;
  created_at: string;
}

interface ActionLog {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  ip_address: string;
  user_agent: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  http_method: string;
  api_path: string;
  request_body: any;
  response_status: number | null;
  details: any;
  created_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const STATUS_LABELS: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
  success: { label: '成功', color: 'success' },
  failed_password: { label: 'パスワード不一致', color: 'error' },
  failed_ip: { label: 'IP制限', color: 'error' },
  failed_not_found: { label: 'ユーザー不明', color: 'error' },
  failed_inactive: { label: '無効アカウント', color: 'warning' },
  logout: { label: 'ログアウト', color: 'info' },
};

const ACTION_LABELS: Record<string, string> = {
  page_view: 'ページ閲覧',
  create: '作成',
  update: '更新',
  delete: '削除',
  download: 'ダウンロード',
  upload: 'アップロード',
  export: 'エクスポート',
  settings_change: '設定変更',
  other: 'その他',
};

const RESOURCE_LABELS: Record<string, string> = {
  account: 'アカウント',
  chatroom: 'チャットルーム',
  chatroom_tag: 'ルームタグ',
  chatroom_note: 'ルームメモ',
  message: 'メッセージ',
  template: 'テンプレート',
  ip_allowlist: 'IP許可リスト',
  fixed_tag: '固定タグ',
  fixed_tag_category: 'タグカテゴリ',
  fixed_tag_option: 'タグオプション',
  report: 'レポート',
  response_time_report: 'レスポンスタイム',
  file: 'ファイル',
  help_article: 'ヘルプ記事',
  sms: 'SMS',
  email: 'メール',
  room_lock: 'ルームロック',
  invitation: '招待',
  stats: '統計',
  analytics: '分析',
  settings: '設定',
  audit_log: '監査ログ',
  unknown: '不明',
};

const ACTION_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default' | 'primary' | 'secondary'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  download: 'primary',
  upload: 'secondary',
  page_view: 'default',
  export: 'primary',
  settings_change: 'warning',
  other: 'default',
};

const HTTP_METHOD_COLORS: Record<string, string> = {
  GET: '#4caf50',
  POST: '#2196f3',
  PUT: '#ff9800',
  DELETE: '#f44336',
  PATCH: '#9c27b0',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const AuditLogs: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  // ログインログ状態
  const [loginLogs, setLoginLogs] = useState<LoginLog[]>([]);
  const [loginPagination, setLoginPagination] = useState<Pagination>({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginFilters, setLoginFilters] = useState({ status: '', ipAddress: '', startDate: '', endDate: '' });

  // アクションログ状態
  const [actionLogs, setActionLogs] = useState<ActionLog[]>([]);
  const [actionPagination, setActionPagination] = useState<Pagination>({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionFilters, setActionFilters] = useState({ action: '', resourceType: '', ipAddress: '', startDate: '', endDate: '' });

  const [error, setError] = useState<string | null>(null);

  const fetchLoginLogs = useCallback(async (page = 1) => {
    setLoginLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(loginPagination.limit) };
      if (loginFilters.status) params.status = loginFilters.status;
      if (loginFilters.ipAddress) params.ipAddress = loginFilters.ipAddress;
      if (loginFilters.startDate) params.startDate = loginFilters.startDate;
      if (loginFilters.endDate) params.endDate = loginFilters.endDate;

      const qs = new URLSearchParams(params).toString();
      const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/audit-logs/login?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) {
        setLoginLogs(data.logs);
        setLoginPagination(data.pagination);
      } else {
        setError(data.message || 'ログインログの取得に失敗しました');
      }
    } catch (err) {
      setError('ログインログの取得に失敗しました');
    } finally {
      setLoginLoading(false);
    }
  }, [loginFilters, loginPagination.limit]);

  const fetchActionLogs = useCallback(async (page = 1) => {
    setActionLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(actionPagination.limit) };
      if (actionFilters.action) params.action = actionFilters.action;
      if (actionFilters.resourceType) params.resourceType = actionFilters.resourceType;
      if (actionFilters.ipAddress) params.ipAddress = actionFilters.ipAddress;
      if (actionFilters.startDate) params.startDate = actionFilters.startDate;
      if (actionFilters.endDate) params.endDate = actionFilters.endDate;

      const qs = new URLSearchParams(params).toString();
      const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/audit-logs/action?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) {
        setActionLogs(data.logs);
        setActionPagination(data.pagination);
      } else {
        setError(data.message || 'アクションログの取得に失敗しました');
      }
    } catch (err) {
      setError('アクションログの取得に失敗しました');
    } finally {
      setActionLoading(false);
    }
  }, [actionFilters, actionPagination.limit]);

  useEffect(() => {
    if (tabIndex === 0) fetchLoginLogs(1);
    else fetchActionLogs(1);
  }, [tabIndex]);

  const handleDownloadCSV = async (type: 'login' | 'action') => {
    const filters = type === 'login' ? loginFilters : actionFilters;
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });

    const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');
    const resp = await fetch(`/api/admin/audit-logs/${type}/csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>監査ログ</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ログイン履歴と操作履歴を確認できます
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<LoginIcon />} iconPosition="start" label="ログインログ" />
        <Tab icon={<ActionIcon />} iconPosition="start" label="アクションログ" />
      </Tabs>

      {/* ログインログ */}
      <TabPanel value={tabIndex} index={0}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={loginFilters.status}
              label="ステータス"
              onChange={(e: SelectChangeEvent) => setLoginFilters(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">全て</MenuItem>
              <MenuItem value="success">成功</MenuItem>
              <MenuItem value="failed_password">パスワード不一致</MenuItem>
              <MenuItem value="failed_ip">IP制限</MenuItem>
              <MenuItem value="failed_not_found">ユーザー不明</MenuItem>
              <MenuItem value="logout">ログアウト</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small" label="IPアドレス" value={loginFilters.ipAddress}
            onChange={e => setLoginFilters(f => ({ ...f, ipAddress: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            size="small" label="開始日" type="date" value={loginFilters.startDate}
            onChange={e => setLoginFilters(f => ({ ...f, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small" label="終了日" type="date" value={loginFilters.endDate}
            onChange={e => setLoginFilters(f => ({ ...f, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" startIcon={<FilterIcon />} onClick={() => fetchLoginLogs(1)}>
            検索
          </Button>
          <Tooltip title="更新"><IconButton size="small" onClick={() => fetchLoginLogs(loginPagination.page)}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadCSV('login')}>
            CSV
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          {loginLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>日時</TableCell>
                  <TableCell>ユーザー</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>IPアドレス</TableCell>
                  <TableCell>失敗理由</TableCell>
                  <TableCell>ユーザーエージェント</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loginLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>ログがありません</TableCell></TableRow>
                ) : loginLogs.map((log) => {
                  const statusInfo = STATUS_LABELS[log.status] || { label: log.status, color: 'default' as const };
                  return (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{log.display_name || log.username}</Typography>
                        {log.display_name && <Typography variant="caption" color="text.secondary">{log.username}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusInfo.label}
                          color={statusInfo.color}
                          icon={log.status === 'success' ? <SuccessIcon /> : log.status === 'logout' ? <LogoutIcon /> : <FailIcon />}
                        />
                      </TableCell>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{log.ip_address}</Typography></TableCell>
                      <TableCell>{log.failure_reason || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={log.user_agent || ''}>
                          <Typography variant="caption" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.user_agent || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div" count={loginPagination.total}
          page={loginPagination.page - 1} rowsPerPage={loginPagination.limit}
          onPageChange={(_, newPage) => fetchLoginLogs(newPage + 1)}
          onRowsPerPageChange={e => { setLoginPagination(p => ({ ...p, limit: parseInt(e.target.value, 10) })); fetchLoginLogs(1); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
        />
      </TabPanel>

      {/* アクションログ */}
      <TabPanel value={tabIndex} index={1}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>アクション</InputLabel>
            <Select
              value={actionFilters.action}
              label="アクション"
              onChange={(e: SelectChangeEvent) => setActionFilters(f => ({ ...f, action: e.target.value }))}
            >
              <MenuItem value="">全て</MenuItem>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>リソース</InputLabel>
            <Select
              value={actionFilters.resourceType}
              label="リソース"
              onChange={(e: SelectChangeEvent) => setActionFilters(f => ({ ...f, resourceType: e.target.value }))}
            >
              <MenuItem value="">全て</MenuItem>
              {Object.entries(RESOURCE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small" label="IPアドレス" value={actionFilters.ipAddress}
            onChange={e => setActionFilters(f => ({ ...f, ipAddress: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            size="small" label="開始日" type="date" value={actionFilters.startDate}
            onChange={e => setActionFilters(f => ({ ...f, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small" label="終了日" type="date" value={actionFilters.endDate}
            onChange={e => setActionFilters(f => ({ ...f, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" startIcon={<FilterIcon />} onClick={() => fetchActionLogs(1)}>
            検索
          </Button>
          <Tooltip title="更新"><IconButton size="small" onClick={() => fetchActionLogs(actionPagination.page)}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownloadCSV('action')}>
            CSV
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          {actionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>日時</TableCell>
                  <TableCell>ユーザー</TableCell>
                  <TableCell>アクション</TableCell>
                  <TableCell>リソース</TableCell>
                  <TableCell>メソッド</TableCell>
                  <TableCell>APIパス</TableCell>
                  <TableCell>IPアドレス</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>詳細</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>ログがありません</TableCell></TableRow>
                ) : actionLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{log.display_name || log.username}</Typography>
                      {log.display_name && <Typography variant="caption" color="text.secondary">{log.username}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={ACTION_LABELS[log.action] || log.action} color={ACTION_COLORS[log.action] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{RESOURCE_LABELS[log.resource_type] || log.resource_type}</Typography>
                      {log.resource_id && (
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {log.resource_id.length > 12 ? log.resource_id.substring(0, 12) + '...' : log.resource_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small" variant="outlined"
                        label={log.http_method}
                        sx={{ fontFamily: 'monospace', fontWeight: 600, borderColor: HTTP_METHOD_COLORS[log.http_method] || '#999', color: HTTP_METHOD_COLORS[log.http_method] || '#999' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.api_path}>
                        <Typography variant="caption" fontFamily="monospace" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.api_path}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Typography variant="body2" fontFamily="monospace">{log.ip_address}</Typography></TableCell>
                    <TableCell>
                      {log.response_status && (
                        <Chip
                          size="small" variant="outlined"
                          label={log.response_status}
                          color={log.response_status < 300 ? 'success' : log.response_status < 400 ? 'info' : 'error'}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {log.details && (
                        <Tooltip title={JSON.stringify(log.details, null, 2)}>
                          <Typography variant="caption" sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                            {log.details.downloadFileName || (typeof log.details === 'object' ? Object.keys(log.details).join(', ') : '')}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div" count={actionPagination.total}
          page={actionPagination.page - 1} rowsPerPage={actionPagination.limit}
          onPageChange={(_, newPage) => fetchActionLogs(newPage + 1)}
          onRowsPerPageChange={e => { setActionPagination(p => ({ ...p, limit: parseInt(e.target.value, 10) })); fetchActionLogs(1); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
        />
      </TabPanel>
    </AdminLayout>
  );
};

export default AuditLogs;
